import { useState, useEffect } from 'react'
import LoginHistoryService, { type LoginEntry } from '../services/LoginHistoryService'

interface LoginHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  username: string
}

const LoginHistoryModal = ({ isOpen, onClose, userId, username }: LoginHistoryModalProps) => {
  const [history, setHistory] = useState<LoginEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [lastDoc, setLastDoc] = useState<any>(null)

  useEffect(() => {
    if (isOpen && userId) {
      // Reset state przy otwieraniu modalu
      setHistory([])
      setHasMore(true)
      setLastDoc(null)
      loadHistory()
    }
  }, [isOpen, userId])

  const loadHistory = async () => {
    setLoading(true)
    try {
      const result = await LoginHistoryService.getLoginHistoryPage(userId, 5) // Tylko 5 ostatnich
      setHistory(result.entries)
      setHasMore(result.hasMore)
      setLastDoc(result.lastDoc)
    } catch (error) {
      console.error('Failed to load login history:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMoreHistory = async () => {
    if (!hasMore || loadingMore || !lastDoc) return
    
    setLoadingMore(true)
    try {
      const result = await LoginHistoryService.getLoginHistoryPage(userId, 5, lastDoc)
      setHistory(prev => [...prev, ...result.entries])
      setHasMore(result.hasMore)
      setLastDoc(result.lastDoc)
    } catch (error) {
      console.error('Failed to load more history:', error)
    } finally {
      setLoadingMore(false)
    }
  }

  const formatDate = (timestamp: any) => {
    try {
      let date: Date
      
      // Handle Firebase Timestamp
      if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
        date = timestamp.toDate()
      } else if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        // Handle Firestore Timestamp-like object
        date = new Date(timestamp.seconds * 1000)
      } else {
        // Handle regular Date or string
        date = new Date(timestamp)
      }
      
      if (isNaN(date.getTime())) return 'Nieznana data'
      
      return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      })
    } catch {
      return 'Nieznana data'
    }
  }

  const getBrowserInfo = (userAgent?: string) => {
    if (!userAgent) return 'Nieznana przeglądarka'
    
    // Simple browser detection
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari'
    if (userAgent.includes('Edg')) return 'Edge'
    if (userAgent.includes('Opera')) return 'Opera'
    
    return 'Inna przeglądarka'
  }

  if (!isOpen) return null

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '700px', padding: '30px' }}>
        <div className="modal-header">
          <h3 style={{ textAlign: 'center', width: '100%' }}>Historia logowań - {username}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ marginTop: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Ładowanie historii logowań...
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Brak zapisanych logowań dla tego użytkownika.
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {history.map((entry, index) => (
                <div 
                  key={entry.id || index}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      {formatDate(entry.timestamp)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Przeglądarka: {getBrowserInfo(entry.userAgent)}
                    </div>
                  </div>
                  <div style={{ fontSize: '24px' }}>
                    🔐
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Przycisk "Załaduj więcej" */}
          {!loading && history.length > 0 && hasMore && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                onClick={loadMoreHistory}
                disabled={loadingMore}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  backgroundColor: loadingMore ? '#f3f4f6' : '#3b82f6',
                  color: loadingMore ? '#6b7280' : 'white',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {loadingMore ? 'Ładowanie...' : 'Załaduj wcześniejsze logowania'}
              </button>
            </div>
          )}
        </div>

        <div style={{ 
          marginTop: '20px', 
          display: 'flex', 
          justifyContent: 'flex-end',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '15px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer'
            }}
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}

export default LoginHistoryModal